import { z } from 'zod';
import { montarCaminho, requisitar } from './client.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import type { components } from './schema.js';
import { analisar } from './validacao.js';

/**
 * Sincronização com o ERP (r1-sync): carga inicial, sincronização periódica,
 * resumo, lotes recebidos e registros descartados.
 *
 * Toda resposta é conferida por zod, e cada schema é conferido contra o
 * OpenAPI em tempo de compilação (`z.ZodType<Esquemas[…]>`).
 */

type Esquemas = components['schemas'];

const CAMINHO_CARGA = montarCaminho('/sincronizacao/carga-inicial');
const CAMINHO_PERIODICA = montarCaminho('/sincronizacao/periodica');
const CAMINHO_RESUMO = montarCaminho('/sincronizacao/resumo');
const CAMINHO_LOTES = montarCaminho('/sincronizacao/lotes');
const CAMINHO_DESCARTADOS = montarCaminho('/sincronizacao/descartados');

const etapaSchema = z.enum(['Categorias', 'Produtos', 'Saldos', 'Lotes']);

export type EtapaDaCarga = z.infer<typeof etapaSchema>;

/** A ordem em que a carga anda. */
export const ETAPAS_DA_CARGA: readonly EtapaDaCarga[] = etapaSchema.options;

const cargaInicialSchema: z.ZodType<Esquemas['CargaInicialResponse']> = z.object({
  status: z.enum(['NaoIniciada', 'EmAndamento', 'Concluida', 'Falhou']),
  etapaAtual: etapaSchema.nullable(),
  emExecucao: z.boolean(),
  categoriasProcessadas: z.number().int().nonnegative(),
  produtosProcessados: z.number().int().nonnegative(),
  saldosProcessados: z.number().int().nonnegative(),
  lotesProcessados: z.number().int().nonnegative(),
  registrosComErro: z.number().int().nonnegative(),
  erros: z.array(z.object({ etapa: etapaSchema, mensagem: z.string() })),
  ultimoErro: z.string().nullable(),
  disparadaPor: z.string().nullable(),
  iniciadaEm: z.string().nullable(),
  concluidaEm: z.string().nullable(),
  sincronizadoEm: z.string().nullable(),
  duracaoMs: z.number().nonnegative().nullable(),
});

export type CargaInicial = Esquemas['CargaInicialResponse'];

export type StatusDaCarga = CargaInicial['status'];

const disparoSchema: z.ZodType<Esquemas['DisparoDaCargaInicialResponse']> = z.object({
  retomada: z.boolean(),
  carga: cargaInicialSchema,
});

export type DisparoDaCarga = Esquemas['DisparoDaCargaInicialResponse'];

const periodicaSchema: z.ZodType<Esquemas['SincronizacaoPeriodicaResponse']> = z.object({
  ativa: z.boolean(),
  intervaloMinutos: z.number().int(),
  emExecucao: z.boolean(),
  sincronizadoEm: z.string().nullable(),
  proximaExecucaoEm: z.string().nullable(),
  falhasConsecutivas: z.number().int().nonnegative(),
  ultimoErro: z.string().nullable(),
});

export type SincronizacaoPeriodica = Esquemas['SincronizacaoPeriodicaResponse'];

export type ConfiguracaoDaPeriodica = Esquemas['ConfigurarSincronizacaoRequest'];

/** Limites do intervalo aceitos pela API (minutos). */
export const INTERVALO_MINIMO = 1;
export const INTERVALO_MAXIMO = 1440;

/** Estado e progresso da carga inicial (só Administrador). */
export async function obterCargaInicial(): Promise<CargaInicial> {
  const corpo = await requisitar(CAMINHO_CARGA);
  return analisar(cargaInicialSchema, corpo, CAMINHO_CARGA);
}

/**
 * Dispara (ou retoma do checkpoint) a carga inicial. Responde 202: o trabalho
 * segue em segundo plano e se acompanha por `obterCargaInicial`.
 */
export async function dispararCargaInicial(): Promise<DisparoDaCarga> {
  const corpo = await requisitar(CAMINHO_CARGA, { metodo: 'POST' });
  return analisar(disparoSchema, corpo, CAMINHO_CARGA);
}

export async function obterSincronizacaoPeriodica(): Promise<SincronizacaoPeriodica> {
  const corpo = await requisitar(CAMINHO_PERIODICA);
  return analisar(periodicaSchema, corpo, CAMINHO_PERIODICA);
}

/** Liga, desliga ou ajusta o intervalo. O PUT leva sempre os dois campos. */
export async function configurarSincronizacaoPeriodica(
  configuracao: ConfiguracaoDaPeriodica,
): Promise<SincronizacaoPeriodica> {
  const corpo = await requisitar(CAMINHO_PERIODICA, {
    metodo: 'PUT',
    corpo: { ativa: configuracao.ativa, intervaloMinutos: configuracao.intervaloMinutos },
  });
  return analisar(periodicaSchema, corpo, CAMINHO_PERIODICA);
}

// ---------------------------------------------------------------------------
// Resumo, lotes e descartados
// ---------------------------------------------------------------------------

export type TipoDeLote = Esquemas['TipoDaSincronizacao'];
export type StatusDoLote = Esquemas['StatusLoteExportacao'];
/** Lotes de PRODUTO (`saldo_lote`), não os lotes da exportação do ERP. */
const lotesDeProdutoSchema = z.object({
  ativos: z.number().int().nonnegative(),
  foraDeUso: z.number().int().nonnegative(),
  ultimaSincronizacaoEm: z.string().nullable(),
});

export type LotesDeProdutoNoResumo = z.infer<typeof lotesDeProdutoSchema>;

/**
 * `lotesDeProduto` é opcional: o middleware antigo não o manda, e o resumo
 * não pode deixar de aparecer por isso. Quando o `openapi.json` trouxer o
 * campo, regenere — o `satisfies` abaixo acusa se o formato divergir.
 */
export type ResumoDaSincronizacao = Esquemas['SincronizacaoResumoResponse'] & {
  readonly lotesDeProduto?: LotesDeProdutoNoResumo | undefined;
};
export type LoteDaSincronizacao = Esquemas['LoteDaSincronizacaoResponse'];
export type RegistroDescartado = Esquemas['RegistroDescartadoResponse'];

const contagem = z.number().int().nonnegative();

const resumoSchema = z.object({
  categorias: z.object({ total: contagem, ativas: contagem, excluidas: contagem }),
  produtos: z.object({ total: contagem, ativos: contagem, excluidos: contagem }),
  saldos: z.object({ total: contagem, ativos: contagem, comDivergenciaPendente: contagem }),
  lotes: z.object({
    recebidos: contagem,
    confirmados: contagem,
    ultimoRecebidoEm: z.string().nullable(),
  }),
  descartados: contagem,
  lotesDeProduto: lotesDeProdutoSchema.optional(),
}) satisfies z.ZodType<Esquemas['SincronizacaoResumoResponse']>;

export const tipoDeLoteSchema = z.enum(['categorias', 'produtos', 'saldos']);

const loteSchema: z.ZodType<Esquemas['LoteDaSincronizacaoResponse']> = z.object({
  id: z.string(),
  tipo: tipoDeLoteSchema,
  /** O identificador da integração no ERP (89 categorias, 5 produtos, 64 saldos). */
  identificadorIntegracao: z.number().int(),
  codFil: z.number().int(),
  numeroLote: z.number().int(),
  status: z.enum(['Recebido', 'Confirmado']),
  quantidadeRegistros: contagem,
  quantidadeExcecoes: contagem,
  recebidoEm: z.string(),
  confirmadoEm: z.string().nullable(),
  tentativasConfirmacao: contagem,
  ultimoErro: z.string().nullable(),
});

const descartadoSchema: z.ZodType<Esquemas['RegistroDescartadoResponse']> = z.object({
  /** `<id da entrada da trilha>:<posição no lote>` — não é UUID. */
  id: z.string(),
  tipo: tipoDeLoteSchema,
  codFil: z.number().int(),
  numeroLote: z.number().int(),
  motivo: z.string(),
  /** Pode vir `{}` (a trilha não tem a chave) ou `{ texto }` (fora do formato `campo=valor`). */
  identificacao: z.record(z.string(), z.string()),
  registradoEm: z.string(),
});

const paginaDeLotesSchema = paginaSchema(loteSchema);
const paginaDeDescartadosSchema = paginaSchema(descartadoSchema);

export async function obterResumoDaSincronizacao(): Promise<ResumoDaSincronizacao> {
  const corpo = await requisitar(CAMINHO_RESUMO);
  return analisar(resumoSchema, corpo, CAMINHO_RESUMO);
}

export type FiltroDeLotes = ParametrosDePagina & {
  readonly tipo?: TipoDeLote;
  readonly codFil?: number;
  readonly status?: StatusDoLote;
};

/** O mais recente primeiro (`recebidoEm`). */
export async function listarLotes(
  filtro: FiltroDeLotes = {},
): Promise<Pagina<LoteDaSincronizacao>> {
  const corpo = await requisitar(CAMINHO_LOTES, {
    consulta: {
      ...parametrosDePagina(filtro),
      tipo: filtro.tipo,
      codFil: filtro.codFil,
      status: filtro.status,
    },
  });
  return analisar(paginaDeLotesSchema, corpo, CAMINHO_LOTES);
}

export type FiltroDeDescartados = ParametrosDePagina & {
  readonly tipo?: TipoDeLote;
  readonly codFil?: number;
};

/** Um item por registro descartado, o mais recente primeiro. */
export async function listarDescartados(
  filtro: FiltroDeDescartados = {},
): Promise<Pagina<RegistroDescartado>> {
  const corpo = await requisitar(CAMINHO_DESCARTADOS, {
    consulta: { ...parametrosDePagina(filtro), tipo: filtro.tipo, codFil: filtro.codFil },
  });
  return analisar(paginaDeDescartadosSchema, corpo, CAMINHO_DESCARTADOS);
}
